/**
 * Informant Validation Utilities
 * ==============================
 *
 * Client-side validation for informant management forms.  These functions
 * run before any backend calls to give the user immediate feedback and
 * avoid unnecessary round-trips.
 *
 * All validators return either an error string or null (no error).
 * The `validateNewInformant` function validates all fields at once and
 * returns a map of field → error string, which is convenient for
 * rendering per-field error messages.
 *
 * The backend performs its own validation (email format, conflict
 * checks, etc.), so these client-side checks are a UX convenience,
 * not a security boundary.
 */

/**
 * Validate an email address format.
 *
 * Uses the same regex pattern as the backend
 * (`/^[^\s@]+@[^\s@]+\.[^\s@]+$/`).  Does not check whether the
 * email is available — that requires a server call.
 *
 * @param {string} email
 * @returns {string|null} Error message or null
 */
export function validateEmail(email) {
  if (!email || !email.trim()) {
    return 'Email address is required.';
  }
  const trimmed = email.trim().toLowerCase();
  // RFC 5321: total address must not exceed 254 characters
  if (trimmed.length > 254) {
    return 'Email address is too long.';
  }
  // Structural guards -- reject patterns that Firebase Auth would reject with
  // INVALID_EMAIL so the user sees an inline form error, not a server 500.
  // These mirror the five guards in validate_email_format (clinician_email_update.py).
  if (/^[.,]/.test(trimmed) || /[.,]$/.test(trimmed)) {
    return 'Please enter a valid email address.';
  }
  if (trimmed.includes('..')) {
    return 'Please enter a valid email address.';
  }
  if ((trimmed.match(/@/g) || []).length !== 1) {
    return 'Please enter a valid email address.';
  }
  const [local, domain] = trimmed.split('@');
  if (local.endsWith('.') || domain.startsWith('.')) {
    return 'Please enter a valid email address.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return 'Please enter a valid email address.';
  }
  return null;
}

/**
 * Validate a name field (first name or last name).
 *
 * Only checks that the field is non-empty after trimming.  Character
 * restrictions (no numbers, no special characters beyond hyphens and
 * apostrophes) are enforced at input time via sanitizeNameInput, which
 * is consistent with how PatientSetupForm handles name fields.
 *
 * @param {string} value
 * @param {string} fieldLabel – e.g. "First name" for error messages
 * @returns {string|null}
 */
export function validateName(value, fieldLabel = 'Name') {
  if (!value || !value.trim()) {
    return `${fieldLabel} is required.`;
  }
  return null;
}

/**
 * Validate the relationship field.
 *
 * @param {string} value
 * @returns {string|null}
 */
export function validateRelationship(value) {
  if (!value || !value.trim()) {
    return 'Relationship is required.';
  }
  return null;
}

/**
 * Validate a life stage selection.
 *
 * @param {string} value – Expected to be 'Adulthood' or 'Childhood'
 * @returns {string|null}
 */
export function validateLifeStage(value) {
  if (!value) {
    return 'Please select a questionnaire to assign.';
  }
  if (value !== 'Adulthood' && value !== 'Childhood') {
    return 'Invalid questionnaire selection.';
  }
  return null;
}

/**
 * Check whether a new informant's email duplicates any existing informant
 * on this patient.  This is a client-side pre-check — the backend also
 * verifies uniqueness via Firebase Auth.
 *
 * When `reuseForLifeStage` is provided (Adult ADHD "Add" flow only), an
 * email that matches an existing informant who does NOT already cover that
 * life stage is allowed — the caller will reuse that person via the
 * reassign path rather than creating a new account.  This option is never
 * passed by Split, Replace, or Change Email (those flows always create a
 * new person and must hard-block on any duplicate).
 *
 * @param {string} email                     – The new informant's email
 * @param {Array}  existingInformants         – Array of { email, lifeStages }
 * @param {Object} [options]
 * @param {string} [options.reuseForLifeStage] – Life stage being added; when
 *   set, allow an email that matches an informant who doesn't already cover
 *   this stage (same-person reuse).
 * @returns {string|null} Error message or null
 */
export function validateEmailNotDuplicate(email, existingInformants = [], options = {}) {
  if (!email) return null;
  const normalised = email.trim().toLowerCase();
  const match = existingInformants.find(
    inf => (inf.email || '').trim().toLowerCase() === normalised
  );
  if (!match) return null;

  // When the Add flow passes a reuse life stage, allow the match as long as
  // the existing informant doesn't already cover that stage.
  if (options.reuseForLifeStage) {
    const alreadyCovers = (match.lifeStages || []).includes(options.reuseForLifeStage);
    if (!alreadyCovers) return null;
  }

  return 'This email address is already in use for this patient.';
}

/**
 * Find an existing informant whose email matches the given address and who
 * does NOT already cover `lifeStage`.  Returns the informant object (for
 * display and reuse) or null.
 *
 * Used by the Adult ADHD Add flow to detect same-person reuse — when a
 * patient wants the same person to answer both adulthood and childhood
 * questionnaires, they can enter that person's email in the Add form and
 * the flow will call reassignQuestionnaires instead of setupNewInformant.
 *
 * @param {string} email          – Email entered in the Add form
 * @param {Array}  informants     – Current informants on this patient
 * @param {string} lifeStage      – The life stage being added
 * @returns {Object|null} The reusable informant, or null
 */
export function findReusableInformantForLifeStage(email, informants = [], lifeStage) {
  if (!email || !lifeStage) return null;
  const normalised = email.trim().toLowerCase();
  return informants.find(inf => {
    const emailMatch = (inf.email || '').trim().toLowerCase() === normalised;
    if (!emailMatch) return false;
    // Reusable only if they don't already cover the target stage
    return !(inf.lifeStages || []).includes(lifeStage);
  }) || null;
}

/**
 * Validate all fields for a new informant at once.
 *
 * Returns an object mapping field names to error strings.  Fields
 * without errors are not included in the result, so consumers can
 * check `Object.keys(errors).length === 0` for overall validity.
 *
 * @param {Object}  fields
 * @param {string}  fields.firstName
 * @param {string}  fields.lastName
 * @param {string}  fields.email
 * @param {string}  fields.relationship
 * @param {string}  [fields.lifeStage] – Required when the flow assigns
 *                  a specific life stage (e.g. ADD, SPLIT).  Omit for
 *                  flows where life stage is implicit.
 * @param {Array}   [existingInformants] – For duplicate email check
 * @param {Object}  [options]
 * @param {boolean} [options.requireLifeStage=false] – When true, lifeStage
 *                  is treated as a required field and validated.
 * @param {string}  [options.reuseForLifeStage] – Passed through to
 *                  validateEmailNotDuplicate to allow same-person reuse
 *                  (Adult ADHD Add flow only).
 * @returns {Object} Map of fieldName → error string
 */
export function validateNewInformant(fields, existingInformants = [], options = {}) {
  const errors = {};

  const firstNameErr = validateName(fields.firstName, 'First name');
  if (firstNameErr) errors.firstName = firstNameErr;

  const lastNameErr = validateName(fields.lastName, 'Last name');
  if (lastNameErr) errors.lastName = lastNameErr;

  const emailErr = validateEmail(fields.email);
  if (emailErr) {
    errors.email = emailErr;
  } else {
    const dupErr = validateEmailNotDuplicate(fields.email, existingInformants, {
      reuseForLifeStage: options.reuseForLifeStage,
    });
    if (dupErr) errors.email = dupErr;
  }

  const relErr = validateRelationship(fields.relationship);
  if (relErr) errors.relationship = relErr;

  // Validate lifeStage when the calling flow requires it or when the
  // caller has explicitly provided a value (prevents accidental empty
  // submissions for flows that present a life-stage selector).
  if (options.requireLifeStage || fields.lifeStage !== undefined) {
    const lifeStageErr = validateLifeStage(fields.lifeStage);
    if (lifeStageErr) errors.lifeStage = lifeStageErr;
  }

  return errors;
}
